import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import useBodyLock from '@sorare/core/src/hooks/useBodyLock';

import { GenericSkinItem } from '../Category/Task';
import { BadgeOverview } from './BadgeOverview';
import { BadgeUnlocked } from './BadgeUnlocked';
import { ExperienceBar, Props as ExperienceBarProps } from './ExperienceBar';
import { ClaimBadgeAnimation_ManagerProgressionCategory } from './__generated__/index.graphql';
import { ExperienceBarStep } from './types';

const FADE_OUT_DURATION = 0.3;
const fadeOut = keyframes`
from {
  opacity: 1
}
to{
  opacity: 0
}`;
const Root = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  backdrop-filter: blur(4px);
  background-color: rgba(var(--c-rgb-neutral-100), 0.6);

  &.ended {
    animation: ${fadeOut} ${FADE_OUT_DURATION}s forwards;
  }
`;
const SkipContainer = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  inset: auto 0 var(--unit) 0;
`;

type Props = Pick<ExperienceBarProps, 'from' | 'to' | 'pointsEarned'> & {
  open: boolean;
  onClose: () => void;
  category: ClaimBadgeAnimation_ManagerProgressionCategory;
  rewards: GenericSkinItem[];
};

export const ClaimBadgeAnimation = ({
  open,
  onClose,
  category,
  from,
  to,
  pointsEarned,
  rewards,
}: Props) => {
  const [step, setStep] = useState(0);
  const [experienceBarStep, setExperienceBarStep] = useState<ExperienceBarStep>(
    ExperienceBarStep.LEVEL
  );
  const [ended, setEnded] = useState(false);

  useBodyLock(open);

  const onEnd = useCallback(() => {
    if (rewards.length > 0 && experienceBarStep !== ExperienceBarStep.REWARDS) {
      setExperienceBarStep(ExperienceBarStep.REWARDS);
    } else {
      setEnded(true);
      setTimeout(onClose, FADE_OUT_DURATION * 1000);
    }
  }, [experienceBarStep, onClose, rewards.length]);

  if (!open) {
    return null;
  }

  return (
    <Root
      className={classNames({
        ended,
      })}
    >
      {step === 0 && <BadgeUnlocked onEnd={() => setStep(1)} />}
      {step === 1 && (
        <BadgeOverview category={category} onEnd={() => setStep(2)} />
      )}
      {step === 2 && (
        <ExperienceBar
          step={experienceBarStep}
          setStep={setExperienceBarStep}
          from={from}
          to={to}
          pointsEarned={pointsEarned}
          onEnd={onEnd}
          rewards={rewards}
        />
      )}
      {experienceBarStep !== ExperienceBarStep.REWARDS && (
        <SkipContainer>
          <Button color="transparent" size="medium" onClick={onEnd}>
            <FormattedMessage
              id="Challenges.ClaimBadgeAnimation.clickToSkip"
              defaultMessage="Click to skip"
            />
          </Button>
        </SkipContainer>
      )}
    </Root>
  );
};

ClaimBadgeAnimation.fragments = {
  managerProgressionCategory: gql`
    fragment ClaimBadgeAnimation_ManagerProgressionCategory on ManagerProgressionCategory {
      id
      name
      ...BadgeOverview_ManagerProgressionCategory
    }
    ${BadgeOverview.fragments.managerProgressionCategory}
  ` as TypedDocumentNode<ClaimBadgeAnimation_ManagerProgressionCategory>,
};
